import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Calendar, Clock, Video, Sparkles, CheckCircle2,
  Heart, Wind, Anchor, Compass,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

// ---- Cloudinary optimizer -------------------------------------------------
const opt = (url, w) => {
  if (!url) return url;
  if (url.includes('res.cloudinary.com') && !url.includes('f_auto')) {
    return url.replace('/upload/', `/upload/f_auto,q_auto${w ? `,w_${w}` : ''}/`);
  }
  return url;
};

// The two images approved by the school for the new masterclass template.
const BIO_IMAGE = 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1778754633/ChatGPT_Image_7_may_2026_18_45_40_6_xvlyr6.png';

// ---- Date helpers ---------------------------------------------------------
const fmtDateMadrid = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Madrid',
    });
  } catch (_) { return ''; }
};
const fmtTimeMadrid = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid',
    });
  } catch (_) { return ''; }
};

// ---- Countdown (discreet) -------------------------------------------------
const useCountdown = (targetIso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  if (diff === 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds };
};

const CountdownStrip = ({ targetIso, tone = 'light' }) => {
  const c = useCountdown(targetIso);
  if (!c) return null;
  const pad = (n) => String(n).padStart(2, '0');
  const isDark = tone === 'dark';
  return (
    <div
      className={`inline-flex items-baseline gap-1.5 text-[11px] sm:text-xs tracking-wide ${isDark ? 'text-[#4a3a1a]' : 'text-white/70'}`}
      data-testid="masterclass-countdown"
    >
      <span className={`text-[10px] uppercase tracking-[0.18em] ${isDark ? 'text-[#8a7428]' : 'text-white/45'}`}>Comienza en</span>
      <span className={`font-mono ${isDark ? 'text-[#3a2e14] font-semibold' : 'text-white/90'}`}>
        {c.days > 0 ? `${c.days}d ` : ''}{pad(c.hours)}:{pad(c.minutes)}:{pad(c.seconds)}
      </span>
    </div>
  );
};

// Poster-style structured countdown: 4 cards (Días · Horas · Min · Seg).
const CountdownCards = ({ targetIso }) => {
  const c = useCountdown(targetIso);
  if (!c) return null;
  const pad = (n) => String(n).padStart(2, '0');
  const cell = (label, value) => (
    <div className="bg-white/75 backdrop-blur-sm border border-[#c9a227]/45 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-center min-w-[56px] sm:min-w-[68px] shadow-[0_6px_20px_rgba(120,90,20,0.08)]">
      <div className="font-heading text-2xl sm:text-3xl leading-none text-[#1a1408]">{value}</div>
      <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.2em] text-[#a88b56] mt-1.5 font-medium">{label}</div>
    </div>
  );
  return (
    <div className="flex flex-col items-center" data-testid="masterclass-countdown">
      <div className="flex items-center justify-center gap-3 text-[10px] sm:text-[11px] font-medium tracking-[0.24em] text-[#a88b56] uppercase mb-3">
        <span className="w-8 sm:w-10 h-px bg-gradient-to-r from-transparent via-[#a88b56] to-transparent" aria-hidden="true" />
        <span>Comienza en</span>
        <span className="w-8 sm:w-10 h-px bg-gradient-to-l from-transparent via-[#a88b56] to-transparent" aria-hidden="true" />
      </div>
      <div className="inline-flex gap-2 sm:gap-2.5">
        {cell('Días', String(c.days))}
        {cell('Horas', pad(c.hours))}
        {cell('Min', pad(c.minutes))}
        {cell('Seg', pad(c.seconds))}
      </div>
    </div>
  );
};

// ---- Component ------------------------------------------------------------
export default function MasterclassLanding() {
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const { slug: slugParam } = useParams();
  const [slug, setSlug] = useState(slugParam || null);
  const [cfg, setCfg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const autoEnrollRan = useRef(false);

  // Resolve slug: URL /:slug or fallback to "the relevant published one"
  useEffect(() => {
    if (slugParam) { setSlug(slugParam); return; }
    api.get('/masterclasses/upcoming')
      .then(r => {
        if (r.data?.masterclass?.slug) setSlug(r.data.masterclass.slug);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [slugParam]);

  // Fetch masterclass data (and enrollment if logged in)
  useEffect(() => {
    if (!slug) return;
    api.get(`/masterclasses/${slug}`)
      .then(r => setCfg(r.data))
      .catch(() => setNotFound(true));
    if (user) {
      api.get(`/masterclasses/${slug}/me`)
        .then(r => setEnrolled(!!r.data.is_enrolled))
        .catch(() => {});
    }
  }, [slug, user]);

  const dateLong = useMemo(() => cfg ? fmtDateMadrid(cfg.date_iso) : '', [cfg]);
  const timeStart = useMemo(() => cfg ? fmtTimeMadrid(cfg.date_iso) : '', [cfg]);
  const timeEnd = useMemo(() => {
    if (!cfg) return '';
    const d = new Date(cfg.date_iso);
    d.setMinutes(d.getMinutes() + (cfg.duration_minutes || 90));
    return fmtTimeMadrid(d.toISOString());
  }, [cfg]);

  // Split the title on ':' → first part in dark, second in gold gradient
  const titleParts = useMemo(() => {
    if (!cfg?.title) return { first: '', second: '' };
    const idx = cfg.title.indexOf(':');
    if (idx === -1) return { first: cfg.title, second: '' };
    return {
      first: cfg.title.slice(0, idx + 1).trim(),
      second: cfg.title.slice(idx + 1).trim(),
    };
  }, [cfg]);

  const isPrimerLimite = slug === 'primer-limite';

  const finalizeEnrollmentRedirect = async () => {
    // Skip onboarding entirely for masterclass registrants: mark the user's
    // onboarding as complete so the App-wide `shouldShowOnboarding` gate lets
    // them into /mi-camino directly. This is idempotent and safe for users
    // who had already completed it.
    if (user && !user.onboarding_completed) {
      try { await completeOnboarding(); } catch (_) { /* non-blocking */ }
    }
    navigate(`/mi-camino?welcome=masterclass&mc=${encodeURIComponent(slug)}`);
  };

  const handleEnroll = async () => {
    if (!slug) return;
    if (!user) {
      navigate(`/register?redirect=${encodeURIComponent(`/masterclass/${slug}?action=enroll`)}`);
      return;
    }
    setEnrolling(true);
    try {
      const res = await api.post(`/masterclasses/${slug}/enroll`);
      setEnrolled(true);
      if (res.data.already_enrolled) {
        toast.info('Ya estabas inscrita. Te llevamos a tu camino.');
      } else {
        toast.success('¡Reservada tu plaza! Te llevamos a tu camino.');
      }
      await finalizeEnrollmentRedirect();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar la inscripción');
    } finally {
      setEnrolling(false);
    }
  };

  // Auto-enroll if returning from login/register with ?action=enroll
  useEffect(() => {
    if (!user || !cfg) return;
    if (autoEnrollRan.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'enroll' && !enrolled) {
      autoEnrollRan.current = true;
      handleEnroll();
    }
  }, [user, cfg, enrolled]);

  // -------- Loading / not-found states --------
  if (notFound) {
    return (
      <div className="pt-24 min-h-screen flex flex-col items-center justify-center bg-[#FDFCFA] px-6 text-center">
        <Sparkles className="w-10 h-10 text-[#c9a227]/60 mb-4" />
        <h1 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-2">No hay masterclass disponible ahora mismo</h1>
        <p className="text-sm text-[hsl(var(--text-secondary))] mb-6">Pero pronto habrá una nueva. Mientras tanto, explora las formaciones.</p>
        <Link to="/formaciones" className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-6 py-3 rounded-full text-sm font-medium">
          Ver formaciones <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  if (!cfg) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[#FDFCFA]">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // CTA is built as a plain JSX expression (not a nested component) to avoid
  // remounting on every render.
  const primaryEnrollClasses = 'inline-flex items-center justify-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-7 py-3.5 rounded-full text-sm sm:text-base font-medium transition-all shadow-lg shadow-[#c9a227]/30';
  const secondaryEnrollClasses = 'inline-flex items-center justify-center gap-2 bg-white/95 hover:bg-white text-[#8a7428] px-7 py-3.5 rounded-full text-sm sm:text-base font-medium transition-all shadow-md border border-[#e8d9a8]';
  const goPanelClasses = 'inline-flex items-center justify-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-7 py-3.5 rounded-full text-sm sm:text-base font-medium transition-all shadow-lg shadow-[#c9a227]/25';

  const cta = (variant, testid) => enrolled ? (
    <Link
      to={`/mi-camino?welcome=masterclass&mc=${encodeURIComponent(slug)}`}
      className={goPanelClasses}
      data-testid={testid ? `${testid}-go-panel` : 'masterclass-go-panel'}
    >
      <CheckCircle2 className="w-4 h-4" />
      Ya estás inscrita · Ir a mi camino
    </Link>
  ) : (
    <Button
      onClick={handleEnroll}
      disabled={enrolling}
      className={variant === 'primary' ? primaryEnrollClasses : secondaryEnrollClasses}
      data-testid={testid || 'masterclass-enroll-btn'}
    >
      {enrolling ? 'Reservando…' : 'Reservar mi plaza gratis'}
      <ArrowRight className="w-4 h-4" />
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#F5EFE6]">
      {/* ============ HERO ============
          Ocupa 100vh. Fondo dorado (LIMITES_hero_fondo) sobre base crema.
          Foto de Herminio a la derecha (bien proporcionada, cabeza completa).
          Texto centrado horizontalmente y distribuido verticalmente.
          Tipografías del proyecto: Playfair Display (título/números),
          Cormorant Garamond (subtítulo itálico), Manrope (párrafos, overline). */}
      <section
        className="relative overflow-hidden min-h-screen flex items-center justify-center px-4 sm:px-5 lg:px-8 pt-20 pb-10 sm:pb-12"
        style={{
          backgroundColor: '#F5EFE6',
          backgroundImage:
            "url('https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto/v1787848325/ChatGPT_Image_27_ago_2026_17_47_29_vehhlj.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Portrait background is HIDDEN — the featured image below already
            contains Herminio's photo, so we avoid duplication. */}

        {/* Content — centered column */}
        <div className="relative z-[5] max-w-[1100px] w-full text-center">

          {/* Overline */}
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/70 backdrop-blur-sm border border-[#c9a227]/30 rounded-full text-[11px] sm:text-xs font-medium tracking-[0.14em] text-[#8a7428] uppercase mb-5 sm:mb-6 shadow-sm"
            data-testid="masterclass-hero-overline"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Masterclass gratuita · Online en directo</span>
          </div>

          {/* Featured image — replaces the title + subtitle + paragraphs.
              Includes title, subtitle and Herminio's portrait baked in. */}
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_0d984e69-b9d3-4602-bf0f-4885fb6742e4/artifacts/2j3ydukx_ChatGPT%20Image%2027%20ago%202026%2C%2019_16_23.png"
            alt={cfg.title}
            className="w-full h-auto block mx-auto mb-6 sm:mb-8 max-w-[1000px]"
            data-testid="masterclass-hero-title"
            loading="eager"
          />

          {/* Fecha/hora chips */}
          <div className="flex flex-wrap justify-center gap-2 mb-6 sm:mb-7" data-testid="masterclass-hero-date">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/75 backdrop-blur-sm border border-[#c9a227]/40 rounded-full text-[12.5px] sm:text-sm text-[#3a2e14] capitalize">
              <Calendar className="w-3.5 h-3.5 text-[#8a7428]" /> {dateLong}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/75 backdrop-blur-sm border border-[#c9a227]/40 rounded-full text-[12.5px] sm:text-sm text-[#3a2e14]">
              <Clock className="w-3.5 h-3.5 text-[#8a7428]" /> {timeStart}–{timeEnd} · Madrid
            </span>
          </div>

          {/* Countdown structured (4 cells) */}
          <div className="mb-6 sm:mb-7 flex justify-center">
            <CountdownCards targetIso={cfg.date_iso} />
          </div>

          {/* CTA — gold pill, coherente con el resto del sitio */}
          <div className="flex flex-col items-center gap-3">
            {enrolled ? (
              <Link
                to={`/mi-camino?welcome=masterclass&mc=${encodeURIComponent(slug)}`}
                className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 rounded-full bg-[#c9a227] hover:bg-[#b89220] text-white font-medium text-sm sm:text-base transition-all shadow-lg shadow-[#c9a227]/30 hover:-translate-y-0.5"
                data-testid="masterclass-go-panel"
              >
                <CheckCircle2 className="w-4 h-4" />
                Ya estás inscrita · Ir a mi camino
              </Link>
            ) : (
              <button
                onClick={handleEnroll}
                disabled={enrolling}
                className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 rounded-full bg-[#c9a227] hover:bg-[#b89220] text-white font-medium text-sm sm:text-base transition-all shadow-lg shadow-[#c9a227]/30 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-wait"
                data-testid="masterclass-enroll-btn"
              >
                {enrolling ? 'Reservando…' : 'Reserva tu plaza gratis'}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <span className="italic text-[#5a4a2a] text-[12.5px] font-[Cormorant_Garamond,serif]">
              Plazas limitadas — no te quedes sin la tuya.
            </span>
          </div>
        </div>
      </section>

      {/* ============ Cuerpo — copy consciente + estructura conversión ============ */}
      <section className="bg-[#fdfcf8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20 space-y-14 sm:space-y-16">

          {slug === 'primer-limite' ? (
            <>
              {/* Intro — el porqué */}
              <article className="space-y-5 text-base sm:text-lg text-[hsl(var(--text-secondary))] leading-relaxed">
                <h2 className="text-3xl sm:text-4xl font-heading text-[hsl(var(--text-primary))] leading-tight">
                  Una masterclass gratuita para <span className="text-[#8a7428]">volver a sentirte dentro de ti</span>
                </h2>
                <p>A veces no perdemos nuestros límites de golpe.</p>
                <p>
                  Los vamos cediendo poco a poco: cuando nos adaptamos demasiado, cuando explicamos
                  más de lo necesario, cuando evitamos incomodar, cuando sostenemos el vínculo a costa
                  de dejar de escucharnos.
                </p>
                <p>El primer límite no empieza diciendo &ldquo;no&rdquo; hacia fuera.<br />
                  Empieza cuando <strong className="font-semibold text-[hsl(var(--text-primary))]">vuelves a sentir dónde estás tú</strong>.
                </p>
              </article>
            </>
          ) : (
            <p className="text-base sm:text-lg text-[hsl(var(--text-secondary))] leading-relaxed whitespace-pre-line">
              {cfg.description}
            </p>
          )}

          {/* Bloque conversión: para quién es */}
          <div className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
              Esta masterclass es para ti si…
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3 text-[15px] sm:text-base text-[hsl(var(--text-secondary))]">
              {[
                { icon: Heart, t: 'Te cuesta poner límites sin sentir culpa.' },
                { icon: Wind, t: 'Te adaptas mucho y después te quedas cansada o desconectada.' },
                { icon: Anchor, t: 'Necesitas proteger tu energía sin dureza ni cierre.' },
                { icon: Compass, t: 'Quieres bajar tu proceso al cuerpo, tus vínculos y tu vida real.' },
              ].map(({ icon: Icon, t }, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-[#eadfbc]/60"
                >
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#faf6ed] flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#c9a227]" strokeWidth={1.75} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Bloque conversión: qué te llevarás */}
          <div className="p-6 sm:p-8 bg-gradient-to-br from-[#faf6ed] via-[#f9f2df] to-[#f5ecd2] border border-[#e8d9a8] rounded-2xl">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#8a7428] font-medium mb-2">Lo que te llevarás</p>
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-5">
              Una experiencia guiada, no una teoría más
            </h2>
            <ul className="space-y-3 text-[15px] sm:text-base text-[hsl(var(--text-secondary))]">
              <li className="flex items-start gap-3"><span className="text-[#c9a227] mt-1.5 text-lg leading-none">·</span>Una comprensión clara de por qué a veces te cuesta poner límites aunque sepas que los necesitas.</li>
              <li className="flex items-start gap-3"><span className="text-[#c9a227] mt-1.5 text-lg leading-none">·</span>Una mirada honesta y amorosa sobre los momentos en los que te adaptas, te explicas de más o te alejas de ti.</li>
              <li className="flex items-start gap-3"><span className="text-[#c9a227] mt-1.5 text-lg leading-none">·</span>Una práctica guiada para volver a tu cuerpo, recuperar tu eje y sentir tu espacio interno.</li>
              <li className="flex items-start gap-3"><span className="text-[#c9a227] mt-1.5 text-lg leading-none">·</span>Gestos simples para empezar a sostenerte desde el primer día — sin culpa, sin rigidez.</li>
              <li className="flex items-start gap-3"><span className="text-[#c9a227] mt-1.5 text-lg leading-none">·</span>Un espacio final para preguntas, integración y orientación sobre tu propio proceso.</li>
            </ul>
          </div>

          {/* Bio / autora con la segunda imagen aprobada */}
          <div className="grid sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-6 sm:gap-8 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden ring-1 ring-[#e8d9a8]/60 shadow-lg">
                <img
                  src={opt(BIO_IMAGE, 700)}
                  alt="Escuela de Consciencia — Herminio Vázquez"
                  className="w-full h-auto block"
                  data-testid="masterclass-bio-image"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="space-y-3 text-[15px] sm:text-base text-[hsl(var(--text-secondary))] leading-relaxed">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#8a7428] font-medium">Quién guía este espacio</p>
              <h3 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] leading-tight">
                Un encuentro sostenido desde la Escuela de Consciencia
              </h3>
              <p>
                No es un taller para aprender técnicas. Es un espacio vivo para reconocer lo que ya sabes,
                bajarlo al cuerpo y dejar de perderte en el vínculo.
              </p>
              <p>
                Trabajamos desde una mirada consciente y encarnada: sin dureza, sin espiritualismo evasivo,
                y sin trucos de motivación. Solo volver a casa, dentro de ti.
              </p>
            </div>
          </div>

          {/* Cierre + CTA final */}
          <div className="bg-gradient-to-br from-[#0f1a26] to-[#2d3748] text-white rounded-2xl p-8 sm:p-10 text-center">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#e8c75a] font-medium mb-3">El primer límite</p>
            <h3 className="text-2xl sm:text-3xl font-heading mb-4 leading-tight">
              El límite no separa. <span className="text-[#e8c75a]">Ordena.</span>
            </h3>
            <p className="text-white/75 mb-2">Un límite sano no nace para alejarte del amor.</p>
            <p className="text-white/75 mb-6">
              Nace para que puedas seguir presente, entera y conectada — sin desaparecer dentro de lo que el otro necesita.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {cta('secondary', 'masterclass-enroll-btn-bottom')}
              <CountdownStrip targetIso={cfg.date_iso} />
            </div>
            <p className="text-[11px] text-white/45 mt-4">
              Gratis · Online en directo · Sin tarjeta · Acceso a la grabación durante 48 h
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}
